import { Bot, MessageSquare, Pencil, Plus } from "lucide-react"
import * as React from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { Agent } from "@/lib/types"

export function AgentsPage() {
  const navigate = useNavigate()
  const t = useT()
  const [agents, setAgents] = React.useState<Agent[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    api
      .listAgents()
      .then(setAgents)
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : t("common.loadAgentsFailed")),
      )
      .finally(() => setLoading(false))
  }, [t])

  return (
    <>
      <PageHeader
        title={t("agents.title")}
        description={t("agents.description")}
        action={
          <Button onClick={() => navigate("/agents/new")}>
            <Plus className="size-4" />
            {t("agents.newAgent")}
          </Button>
        }
      />

      <div className="p-6">
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        ) : agents.length === 0 ? (
          <EmptyState
            icon={Bot}
            title={t("agents.empty.title")}
            description={t("agents.empty.description")}
            actionLabel={t("common.createAgent")}
            onAction={() => navigate("/agents/new")}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {agents.map((agent) => (
              <Card key={agent.id} className="flex flex-col">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CardTitle className="truncate">{agent.name}</CardTitle>
                    {!agent.is_active && (
                      <Badge variant="secondary">{t("common.status.disabled")}</Badge>
                    )}
                  </div>
                  <CardDescription className="line-clamp-2">
                    {agent.description || agent.system_prompt || t("agents.noDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">{agent.model}</Badge>
                    <Badge variant="secondary">
                      {t("common.toolCount", { count: agent.tool_count })}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/chat?agent=${agent.id}`)}
                    >
                      <MessageSquare className="size-4" />
                      {t("agents.chatAction")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/agents/${agent.id}`)}
                      aria-label={t("agents.editAria", { name: agent.name })}
                    >
                      <Pencil className="size-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
